import React, { useEffect, useRef } from "react";
import "./CrosswordGame.css";
import { CASEBOOK_CASES, CrosswordResult, MAX_GLASSES } from "./crosswordGame.ts";
import { findBySlug } from "../../Shared/Seo/routeSeo.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

interface CrosswordGameSummaryProps {
    result: CrosswordResult;
    /** Best casebook at this rank on this device, after this one. */
    best: number | null;
    isNewBest: boolean;
    /** Where the practice behind this rank lives, by slug; looked up in the manifest. */
    practiceSlugs: string[];
    onPlayAgain(): void;
}

/**
 * The end of a casebook (ADR 0134 §5): magnifying glasses out of 15, the cases solved
 * without Ask Holmes, the wrong blanks Check found and the blanks Holmes filled, the best
 * at this rank, and the worksheets that practise what the rank was about. Takes the focus
 * when it appears, so a keyboard or screen reader user lands on it rather than on a board
 * that has just closed.
 */
export function CrosswordGameSummary({ result, best, isNewBest, practiceSlugs, onPlayAgain }: CrosswordGameSummaryProps) {
    const t = useTranslation();
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        ref.current?.focus({ preventScroll: true });
    }, []);

    const links = practiceSlugs.flatMap((slug) => {
        const route = findBySlug(slug);
        return route ? [{ slug: route.slug, text: route.h1 ?? route.slug }] : [];
    });

    return (
        <div className="cwFile cwSummary" ref={ref} tabIndex={-1}>
            <p className="cwSummaryHeading">{t("flashCards.crosswordFinal")}</p>
            <p className="cwSummaryScore">{result.glasses}</p>
            <p className="cwSummaryCaption">{format(t("flashCards.crosswordScoreCaptionTemplate"), { max: MAX_GLASSES })}</p>
            <p className="cwSummaryCounts">
                {format(t("flashCards.crosswordSummaryUnaidedTemplate"), { n: result.unaided, total: CASEBOOK_CASES })}
            </p>
            {result.wrong > 0 && (
                <p className="cwSummaryCounts">{format(t("flashCards.crosswordSummaryWrongTemplate"), { n: result.wrong })}</p>
            )}
            {result.skipped > 0 && (
                <p className="cwSummaryCounts">
                    {format(t("flashCards.crosswordSummaryHolmesTemplate"), { n: result.skipped })}
                </p>
            )}

            {best !== null && (
                <p className={`cwSummaryBest${isNewBest ? " cwSummaryBest--new" : ""}`}>
                    {isNewBest ? t("flashCards.newBest") : format(t("flashCards.yourBestTemplate"), { n: best })}
                </p>
            )}

            <button type="button" className="btn btn-primary cwPlayAgain" onClick={onPlayAgain}>
                {t("flashCards.playAgain")}
            </button>

            {links.length > 0 && (
                <div className="cwPractice">
                    <h3>{t("flashCards.crosswordPracticeHeading")}</h3>
                    <ul>
                        {links.map((link) => (
                            <li key={link.slug}>
                                <a href={link.slug}>{link.text}</a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default CrosswordGameSummary;
